import React, { useState, Suspense, useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls, Environment, Html } from '@react-three/drei';

// NOTE: You would typically include the font 'Orbitron' via a global CSS file or HTML <link>.
// For this self-contained code, we include a <style> tag in the render method to import it.

// --- 1. STYLE LOGIC & RESPONSIVENESS ---

// Add keyframes for the fade-in animation (for text content)
const fadeIn = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// Helper to get dynamic styles based on screen width (simulating media queries)
const getStyles = (isMobile) => ({
    // Base container with a cool font and dark gradient
    container: {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row', // Column on mobile, row on desktop
        minHeight: '100vh',
        width: '100%',
        fontFamily: "'Orbitron', sans-serif", 
        color: '#E0F7FA', // Light cyan text
        background: 'linear-gradient(135deg, #000000ff 0%, #000000 100%)', // Subtle dark gradient
    },
    
    // Game Banner section
    gameBanner: {
        flex: isMobile ? 'none' : 1, // Full width on mobile, half on desktop
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '40px 20px' : '20px',
        minHeight: isMobile ? 'auto' : '100%',
        order: isMobile ? 1 : 0, // Puts banner at the bottom of the main content on mobile
    },
    
    // Banner Image - subtle glow and transition
    bannerImage: {
        maxWidth: isMobile ? '80%' : '100%', 
        maxHeight: isMobile ? '40vh' : 'auto', 
        width: 'auto',
        objectFit: 'contain',
       
    },
    
    // Announcement Content section
    announcementContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: isMobile ? 'center' : 'flex-start', // Center text on mobile
        padding: isMobile ? '60px 20px 20px 20px' : '80px',
        textAlign: isMobile ? 'center' : 'left',
        order: isMobile ? 0 : 1, // Puts content at the top on mobile
        animation: 'fadeIn 1s ease-out',
    },
    title: {
        fontSize: isMobile ? '2.5em' : '4em',
        marginBottom: '0.15em',
        color: '#A06CD5', // Vibrant purple
        textShadow: '0 0 15px rgba(160, 108, 213, 0.8), 0 0 5px rgba(255, 255, 255, 0.5)',
        letterSpacing: '2px',
        lineHeight: 1.1,
    },
    subtitle: {
        fontSize: isMobile ? '1.5em' : '2.2em',
        marginBottom: '1em',
        color: '#00D4FF', // Light blue/cyan
        textTransform: 'uppercase',
        fontWeight: 300,
    },
    message: {
        fontSize: isMobile ? '1em' : '1.2em',
        lineHeight: '1.7',
        marginBottom: '2.5em',
        maxWidth: '600px',
    },
    // Neon Button style
    previewBtn: {
        padding: '18px 35px',
        fontSize: '1.2em',
        fontWeight: 'bold',
        color: '#0A192F', // Dark text on neon
        backgroundColor: '#00F0B5', // Neon green
        border: '3px solid #00F0B5',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        boxShadow: '0 0 10px #00F0B5, 0 0 20px rgba(0, 240, 181, 0.6)',
        textTransform: 'uppercase',
    },
    // Model Viewer styles
    viewerContainer: {
        width: '100%',
        height: '100vh',
        background: 'radial-gradient(circle, #1a1a2e 0%, #000000 100%)', // More interesting background
    },
    closeBtn: {
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        padding: '12px 20px',
        fontSize: '1em',
        fontWeight: 'bold',
        color: '#fff',
        backgroundColor: 'rgba(255, 50, 50, 0.8)',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        boxShadow: '0 0 10px rgba(255, 50, 50, 0.4)',
    }
});


// --- 2. 3D MODEL COMPONENT ---
function Model({ url }) {
    // useGLTF handles loading the model data
    const { scene } = useGLTF(url);
    const ref = useRef();
    
    // Scale the model down (common fix for glTF files which are often too large)
    const scaleFactor = 0.5; 
    scene.scale.set(scaleFactor, scaleFactor, scaleFactor);
    
    // Gentle continuous spin
    useFrame(() => {
        if (ref.current) {
            ref.current.rotation.y += 0.005; 
        }
    });

    return <primitive object={scene} ref={ref} />;
}


// --- 3. MODEL VIEWER COMPONENT ---
const ModelViewer = ({ modelUrl, onClose }) => {
    // Use desktop styles for the fullscreen viewer
    const styles = getStyles(false); 

    return (
        <div style={styles.viewerContainer}>
            <button 
                onClick={onClose} 
                style={styles.closeBtn}
                // Hover effects for the close button
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 100, 100, 0.9)';
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 100, 100, 0.8)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 50, 50, 0.8)';
                    e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 50, 50, 0.4)';
                }}
            >
                &times; Close Preview
            </button>

            <Canvas camera={{ position: [0, 1, 3], fov: 50 }}>
                
                {/* Lighting setup for a dramatic look */}
                <pointLight position={[5, 5, 5]} intensity={2} color="#00D4FF" />
                <pointLight position={[-5, -5, -5]} intensity={1.5} color="#A06CD5" />
                <ambientLight intensity={0.4} />

                {/* Fallback loader using the Html component from drei */}
                <Suspense fallback={<Html center style={{ color: 'white' }}>Loading 3D Model...</Html>}>
                    
                    <Model url={modelUrl} />

                    {/* OrbitControls allows user interaction (360-degree rotation, zoom) */}
                    <OrbitControls 
                        enableZoom={true}
                        enablePan={false}
                        minDistance={1.5}
                        maxDistance={5}
                    />
                    
                    {/* Environment for reflections and background lighting */}
                    <Environment preset="studio" blur={0.5} /> 
                
                </Suspense>
            </Canvas>
        </div>
    );
};


// --- 4. MAIN GAME COMPONENT (Layout and State) ---
const GameComponent = () => {
    const [showModelViewer, setShowModelViewer] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
    // Determine if we should use the mobile layout
    const isMobile = windowWidth < 768; // Standard breakpoint for tablet/mobile

    // Effect to handle window resizing for responsiveness
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Memoize the styles to only re-calculate when isMobile changes
    const styles = useMemo(() => getStyles(isMobile), [isMobile]);

    // IMPORTANT: Update these paths to your local files
    const GLB_MODEL_URL ='/futuristic+armor+suit+3d+model.glb'; 
    const DUMMY_BANNER_URL = '/Untitled-design-2--unscreen.gif';

    if (showModelViewer) {
        return (
            <ModelViewer 
                modelUrl={GLB_MODEL_URL} 
                onClose={() => setShowModelViewer(false)} 
            />
        );
    }

    // Announcement view
    return (
        // Global style insertion for font import and keyframes
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&display=swap');
                ${fadeIn}
            `}</style>

            <div style={styles.container}>
                {/* Left/Bottom side: Game Banner */}
                <div style={styles.gameBanner}>
                    <img 
                        src={DUMMY_BANNER_URL}
                        alt="Game Logo and Title"
                        style={styles.bannerImage} 
                        // Enhanced hover effect for the banner image
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                </div>
                
                {/* Right/Top side: Announcement and Preview Button */}
                <div style={styles.announcementContent}>
                    <h1 style={styles.title}> Cosmic Clash: <br/> Horizon Zero </h1>
                    <h2 style={styles.subtitle}>Prepare for Launch!</h2>
                    <p style={styles.message}>
                        The ultimate space strategy game is coming soon. Experience the future of galactic warfare with our custom-designed **futuristic armor suit**. Click below to launch the interactive 3D preview!
                    </p>
                    
                    {/* Button with neon glow and hover effects */}
                    <button 
                        style={styles.previewBtn} 
                        onClick={() => setShowModelViewer(true)}
                        // Hover in effect
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#00CC99';
                            e.currentTarget.style.transform = 'scale(1.02) translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 0 15px #00F0B5, 0 0 30px rgba(0, 240, 181, 0.8)';
                        }}
                        // Hover out effect
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#00F0B5';
                            e.currentTarget.style.transform = 'scale(1) translateY(0)';
                            e.currentTarget.style.boxShadow = '0 0 10px #00F0B5, 0 0 20px rgba(0, 240, 181, 0.6)';
                        }}
                    >
                        ✨ Preview 3D Asset
                    </button>
                </div>
            </div>
        </>
    );
};

export default GameComponent;